import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { AnalyticsTrigger, Button } from 'telia-front-react';
import { resolveAddressCode } from '../../../address/AddressUtil';
import config from '../../../Config';
import InternetCalculatorStore from '../../../stores/InternetCalculatorStore';
import LanguageStore from '../../../stores/LanguageStore';
import { Segment, SEGMENT_BUSINESS } from '../../../stores/SegmentStore';
import SsoStore from '../../../stores/SsoStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { InternetCalculatorState, ProductCard } from '../InternetCalculatorInterfaces';

interface Props {
  calculatorKey: string;
  card: ProductCard;
  segment?: Segment;
  internetCalculatorStore?: InternetCalculatorStore;
  languageStore?: LanguageStore;
  ssoStore?: SsoStore;
}

@observer
class InternetCalculatorSubmitButton extends React.Component<Props, {}> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  get calculatorState(): InternetCalculatorState {
    const { internetCalculatorStore, calculatorKey } = this.props;
    return internetCalculatorStore.getState(calculatorKey);
  }

  private submit = (): void => {
    const { internetCalculatorStore, calculatorKey, card } = this.props;
    internetCalculatorStore.setSelectedInternet(calculatorKey, card);
    const isExternal = this.isExternalLink();

    if (card.partner || !isExternal) {
      internetCalculatorStore.setOrderFormOpen(calculatorKey, true);
    }
  };

  private resolveAddressCode = (): string => {
    const selectedAddress = this.calculatorState.address;
    const selectedRoom = this.calculatorState.room;

    return resolveAddressCode(selectedAddress, selectedRoom);
  };

  private resolveElionOrderPath = (): string => {
    const {
      calculatorKey,
      internetCalculatorStore,
      languageStore: { currentLanguage }
    } = this.props;
    const elionOrderPath = internetCalculatorStore.getState(calculatorKey).propState.elionOrderPath;
    if (elionOrderPath) {
      return elionOrderPath;
    }
    return currentLanguage === 'et' ? '/eraklient/iseteenindus/ekodu' : '/chastnyj/samoobsluzhivanie/rynok';
  };

  private resolveOfferSpeedLabels = (): string => {
    const { card } = this.props;

    const offerSpeed = /(\d+[_/]\d+)/.exec(card.productOfferingCode)[0];
    if (offerSpeed === '1000_1000' || offerSpeed === '1000/1000') {
      return '1G-1G';
    }
    const uploadDownloadSpeed = offerSpeed.split(new RegExp('[_/]'));
    return `${uploadDownloadSpeed[0]}M-${uploadDownloadSpeed[1]}M`;
  };

  private resolveLteInternetParams = (): string => {
    const selectedAddress = this.calculatorState.address;
    const { card } = this.props;
    const roomValue = this.calculatorState.room;
    const context = this.props.segment === 'BUSINESS' ? '&context=business' : '';

    return (
      `?productOfferingCode=${card.productOfferingCode}&addressData=` +
      `${selectedAddress.id}|${selectedAddress.buildingId}|${roomValue?.roomId ? roomValue.roomId : ''}|${roomValue?.roomName ? roomValue.roomName : ''}${context}`
    );
  };

  private resolveFixBaseParams = (): string => {
    const {
      languageStore: { currentLanguageAlpha3Code }
    } = this.props;
    return `?selectedTab=INTERNET&VK_LANG=${currentLanguageAlpha3Code}&addressCode=${this.resolveAddressCode()}`;
  };

  private resolveFixNetParams = (): string => {
    return `&selectedInternet=119565__${this.resolveOfferSpeedLabels()}`;
  };

  private resolveButtonUrl = (): string => {
    const { internetCalculatorStore, calculatorKey, ssoStore } = this.props;
    const isCable = internetCalculatorStore.isCable(calculatorKey);

    if (!isCable) {
      return `${config.teliaWebSelfServiceUrl}/teenused/internet/asukoht${this.resolveLteInternetParams()}`;
    } else {
      if (ssoStore.isDealer) {
        if (ssoStore.customerId && ssoStore.customer.elionCustomerId) {
          return config.argosCustomerViewUrl.replace(/elionCustomerId/g, `${ssoStore.customer.elionCustomerId}`);
        }
        return;
      }
      const elionBaseUrl = config.elionWebUrl + this.resolveElionOrderPath() + this.resolveFixBaseParams();
      return elionBaseUrl + this.resolveFixNetParams();
    }
  };

  private isExternalLink = (): boolean => {
    const { internetCalculatorStore, calculatorKey, segment, card } = this.props;
    const isCable = internetCalculatorStore.isCable(calculatorKey);
    const isB2B = segment === SEGMENT_BUSINESS;
    if (!!card && !card.partner) {
      return !(isCable && isB2B);
    }
  };

  public render() {
    const translate = this.context.translate;
    const { card } = this.props;
    const isExternalLink = this.isExternalLink();

    return (
      <AnalyticsTrigger trigger={{ id: `CTA -${isExternalLink ? '' : ' Form -'} ${card?.productOfferingCode || ''}`, callback: 'onClick' }}>
        <Button
          url={isExternalLink ? this.resolveButtonUrl() : null}
          target="_blank"
          block={true}
          className="text__margin-double"
          text={translate('internetAndTvCalculator.submitButton')}
          type="expressive"
          iconRight="#arrow-right"
          onClick={this.submit}
        />
      </AnalyticsTrigger>
    );
  }
}

export default inject('internetCalculatorStore', 'languageStore', 'ssoStore')(InternetCalculatorSubmitButton);
